import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { RequirementsData } from "@/types/fact-finder";

interface RequirementsSectionProps {
  data: RequirementsData | null;
  onSave: (data: RequirementsData) => Promise<void>;
  onNext: () => void;
}

const defaultData: RequirementsData = {
  primaryReason: "",
  desiredFeatures: {
    variableRate: false,
    fixedRate: false,
    lineOfCredit: false,
    principalAndInterest: false,
    extraRepaymentsAllowed: false,
    redraw: false,
    offsetAccount: false,
    packagedWithCreditCard: false,
    notSure: false,
  },
  seekingInterestOnly: false,
  interestOnlyReason: "",
  expectedContractLength: "not_sure",
  hasDefaultsOrJudgements: false,
  allCommitmentsUpToDate: true,
  concernAboutRisingRates: "not_sure",
  expectsFinancialChanges: false,
  financialChangesDescription: "",
  wantsFinancialAdviserIntro: false,
};

const primaryReasons = [
  "Purchasing owner-occupied home",
  "Purchasing investment property",
  "Refinancing for better rate",
  "Refinancing to consolidate debt",
  "Refinancing to access equity",
  "Construction loan",
  "Bridging loan",
  "Other",
];

const featureLabels: Record<keyof RequirementsData["desiredFeatures"], string> = {
  variableRate: "Variable Rate",
  fixedRate: "Fixed Rate",
  lineOfCredit: "Line of Credit",
  principalAndInterest: "Principal & Interest",
  extraRepaymentsAllowed: "Extra Repayments Allowed",
  redraw: "Redraw Facility",
  offsetAccount: "Offset Account",
  packagedWithCreditCard: "Packaged with Credit Card",
  notSure: "Not Sure / Need Advice",
};

export function RequirementsSection({ data, onSave, onNext }: RequirementsSectionProps) {
  const [formData, setFormData] = useState<RequirementsData>(data || defaultData);
  const [isSaving, setIsSaving] = useState(false);

  const updateField = (field: keyof RequirementsData, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const toggleFeature = (feature: keyof RequirementsData["desiredFeatures"]) => {
    setFormData((prev) => ({
      ...prev,
      desiredFeatures: {
        ...prev.desiredFeatures,
        [feature]: !prev.desiredFeatures[feature],
      },
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(formData);
      onNext();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-serif">Loan Requirements & Objectives</h2>
        <p className="text-muted-foreground mt-1">
          Help us understand your goals and preferences so we can find the best loan for you.
        </p>
      </div>

      {/* Primary Reason */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Primary Purpose</h3>
        <div className="space-y-2">
          <label className="text-sm font-medium">
            What is the primary reason for this loan?
          </label>
          <Select
            value={formData.primaryReason}
            onValueChange={(v) => updateField("primaryReason", v)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select primary reason" />
            </SelectTrigger>
            <SelectContent>
              {primaryReasons.map((reason) => (
                <SelectItem key={reason} value={reason}>
                  {reason}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Desired Features */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Desired Loan Features</h3>
        <p className="text-sm text-muted-foreground">
          Select any features that are important to you. Select multiple if applicable.
        </p>

        <div className="grid md:grid-cols-3 gap-3">
          {(Object.keys(featureLabels) as Array<keyof RequirementsData["desiredFeatures"]>).map(
            (feature) => (
              <label
                key={feature}
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  formData.desiredFeatures[feature]
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/50"
                }`}
              >
                <input
                  type="checkbox"
                  checked={formData.desiredFeatures[feature]}
                  onChange={() => toggleFeature(feature)}
                  className="w-4 h-4 rounded"
                />
                <span className="text-sm">{featureLabels[feature]}</span>
              </label>
            )
          )}
        </div>
      </div>

      {/* Interest Only */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Repayment Preference</h3>

        <div className="p-4 bg-muted/30 rounded-lg space-y-4">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="interestOnly"
              checked={formData.seekingInterestOnly}
              onChange={(e) => updateField("seekingInterestOnly", e.target.checked)}
              className="w-5 h-5 rounded border-gray-300"
            />
            <label htmlFor="interestOnly" className="text-sm font-medium">
              I am interested in interest-only repayments
            </label>
          </div>

          {formData.seekingInterestOnly && (
            <div className="ml-8 space-y-2">
              <label className="text-sm font-medium">Why do you want interest-only?</label>
              <Input
                value={formData.interestOnlyReason || ""}
                onChange={(e) => updateField("interestOnlyReason", e.target.value)}
                placeholder="e.g. Investment strategy, cash flow management"
              />
            </div>
          )}
        </div>
      </div>

      {/* Expected Contract Length */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Expected Timeframe</h3>

        <div className="space-y-2">
          <label className="text-sm font-medium">
            How long do you expect to keep this loan?
          </label>
          <div className="flex flex-wrap gap-3">
            {[
              { value: "short", label: "Less than 3 years" },
              { value: "medium", label: "3-7 years" },
              { value: "long", label: "7+ years" },
              { value: "not_sure", label: "Not sure" },
            ].map((option) => (
              <label
                key={option.value}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-colors ${
                  formData.expectedContractLength === option.value
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/50"
                }`}
              >
                <input
                  type="radio"
                  name="contractLength"
                  checked={formData.expectedContractLength === option.value}
                  onChange={() =>
                    updateField(
                      "expectedContractLength",
                      option.value as RequirementsData["expectedContractLength"]
                    )
                  }
                  className="w-4 h-4"
                />
                <span className="text-sm">{option.label}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Credit History */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Credit History</h3>

        <div className="space-y-4 p-4 bg-muted/30 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">
                Have you ever had any defaults, judgements, or bankruptcies?
              </p>
              <p className="text-xs text-muted-foreground">
                This includes paid defaults or discharged bankruptcies
              </p>
            </div>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="defaults"
                  checked={!formData.hasDefaultsOrJudgements}
                  onChange={() => updateField("hasDefaultsOrJudgements", false)}
                  className="w-4 h-4"
                />
                <span className="text-sm">No</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="defaults"
                  checked={formData.hasDefaultsOrJudgements}
                  onChange={() => updateField("hasDefaultsOrJudgements", true)}
                  className="w-4 h-4"
                />
                <span className="text-sm">Yes</span>
              </label>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">
                Are all your current financial commitments up to date?
              </p>
              <p className="text-xs text-muted-foreground">
                Including loans, credit cards, and bills
              </p>
            </div>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="upToDate"
                  checked={formData.allCommitmentsUpToDate}
                  onChange={() => updateField("allCommitmentsUpToDate", true)}
                  className="w-4 h-4"
                />
                <span className="text-sm">Yes</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="upToDate"
                  checked={!formData.allCommitmentsUpToDate}
                  onChange={() => updateField("allCommitmentsUpToDate", false)}
                  className="w-4 h-4"
                />
                <span className="text-sm">No</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Risk Tolerance */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Interest Rate Risk</h3>

        <div className="space-y-2">
          <label className="text-sm font-medium">
            How concerned are you about potential interest rate increases?
          </label>
          <div className="flex flex-wrap gap-3">
            {[
              { value: "not_concerned", label: "Not concerned" },
              { value: "a_little", label: "A little concerned" },
              { value: "very_concerned", label: "Very concerned" },
              { value: "not_sure", label: "Not sure" },
            ].map((option) => (
              <label
                key={option.value}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-colors ${
                  formData.concernAboutRisingRates === option.value
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/50"
                }`}
              >
                <input
                  type="radio"
                  name="rateRisk"
                  checked={formData.concernAboutRisingRates === option.value}
                  onChange={() =>
                    updateField(
                      "concernAboutRisingRates",
                      option.value as RequirementsData["concernAboutRisingRates"]
                    )
                  }
                  className="w-4 h-4"
                />
                <span className="text-sm">{option.label}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Future Changes */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Future Financial Changes</h3>

        <div className="p-4 bg-muted/30 rounded-lg space-y-4">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="futureChanges"
              checked={formData.expectsFinancialChanges}
              onChange={(e) => updateField("expectsFinancialChanges", e.target.checked)}
              className="w-5 h-5 rounded border-gray-300"
            />
            <label htmlFor="futureChanges" className="text-sm font-medium">
              I anticipate changes to my financial situation in the next 12 months
            </label>
          </div>

          {formData.expectsFinancialChanges && (
            <div className="ml-8 space-y-2">
              <label className="text-sm font-medium">Please describe the expected changes</label>
              <Textarea
                value={formData.financialChangesDescription || ""}
                onChange={(e) => updateField("financialChangesDescription", e.target.value)}
                placeholder="e.g. Starting a family, changing jobs, retirement, maternity leave"
                rows={3}
              />
            </div>
          )}
        </div>
      </div>

      {/* Financial Adviser */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Additional Services</h3>

        <div className="p-4 bg-muted/30 rounded-lg">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="financialAdviser"
              checked={formData.wantsFinancialAdviserIntro}
              onChange={(e) => updateField("wantsFinancialAdviserIntro", e.target.checked)}
              className="w-5 h-5 rounded border-gray-300"
            />
            <label htmlFor="financialAdviser" className="text-sm">
              I would like an introduction to a financial adviser for wealth planning and insurance
            </label>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end pt-4 border-t">
        <Button onClick={handleSave} disabled={isSaving} size="lg">
          {isSaving ? "Saving..." : "Save & Continue to Consent"}
        </Button>
      </div>
    </div>
  );
}
